import React from 'react'
import { 
  Box, 
  Typography, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Button,
  useTheme
} from '@mui/material'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import { useHandleChangeRoute } from 'data/hooks'

const ContactSection = () => {
  const theme = useTheme()
  const handleChangeRoute = useHandleChangeRoute()

  const handleProviderSignup = () => {
    handleChangeRoute('/register/physicians')()
  }

  const handleEmployerSignup = () => {
    handleChangeRoute('/register/hospitals')()
  }

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 10 },
        backgroundColor: theme.palette.background.dark,
        color: theme.palette.common.white,
        position: 'relative',
        clipPath: { xs: 'none', sm: 'polygon(0 0, 40% 0, 45% 40px, 55% 40px, 60% 0, 100% 0, 100% 100%, 0 100%)' }, // Notched top on desktop only
        paddingTop: { xs: '40px', sm: '80px' } // Less padding on mobile
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section */}
        <Grid container spacing={{ xs: 3, sm: 4 }} sx={{ mb: { xs: 6, sm: 8 } }}>
          {/* Left Side - Contact Us Title */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                lineHeight: 1.2,
                color: theme.palette.common.white
              }}
            >
              Start Your Medical Career<br />
              Journey Today
            </Typography>
          </Grid>
          
          {/* Right Side - Description */}
          <Grid item xs={12} md={6}>
            <Box sx={{ pt: { xs: 0, md: 2 } }}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                  color: theme.palette.common.white
                }}
              >
                Whether you're a provider looking for your next opportunity or a health system seeking top talent, we're here to help.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Cards Section */}
        <Grid container spacing={4}>
          {/* Provider Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'transparent',
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: '16px',
                color: theme.palette.common.white,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.shadows.colored.secondary
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Icon */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.common.white
                    }}
                  >
                    <LightbulbOutlinedIcon />
                  </Box>
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    fontSize: '1.5rem'
                  }}
                >
                  Take the Next Step in Your Career
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    lineHeight: 1.6,
                    fontSize: '1rem'
                  }}
                >
                  Explore curated, recruiter-free job listings and connect directly with hospitals across the country.
                </Typography>

                {/* Button */}
                <Button
                  variant="contained"
                  onClick={handleProviderSignup}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '8px',
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: theme.palette.shadows.colored.primary,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: theme.palette.shadows.colored.primaryHover
                    }
                  }}
                >
                  🩺 Sign Up as Clinician
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Employer Card */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                backgroundColor: 'transparent',
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: '16px',
                color: theme.palette.common.white,
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.shadows.colored.secondary
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Icon */}
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.common.white
                    }}
                  >
                    <LightbulbOutlinedIcon />
                  </Box>
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    fontSize: '1.5rem'
                  }}
                >
                  Let's Simplify Your Hiring Process
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    lineHeight: 1.6,
                    fontSize: '1rem'
                  }}
                >
                  Get direct access to qualified, verified providers without third-party fees or delays.
                </Typography>

                {/* Button */}
                <Button
                  variant="contained"
                  onClick={handleEmployerSignup}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 600,
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.5, sm: 2 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    textTransform: 'none',
                    borderRadius: '8px',
                    width: { xs: '100%', sm: 'auto' },
                    boxShadow: theme.palette.shadows.colored.primary,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                      boxShadow: theme.palette.shadows.colored.primaryHover
                    }
                  }}
                >
                  🏥 Sign Up as Health System
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Single Contact Us Section */}
        <Box sx={{ textAlign: 'center', mt: { xs: 6, sm: 8 }, pt: { xs: 4, sm: 6 }, borderTop: `1px solid ${theme.palette.grey[800]}` }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.1rem',
              mb: 3,
              color: theme.palette.common.white
            }}
          >
            Have questions? Need help getting started?
          </Typography>
          
          <Button
            variant="contained"
            onClick={() => { window.location.href = '/contact' }}
            sx={{
              backgroundColor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              fontWeight: 700,
              px: { xs: 4, sm: 6 },
              py: { xs: 2, sm: 2.5 },
              fontSize: { xs: '1.1rem', sm: '1.2rem' },
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: theme.palette.shadows.colored.primary,
              border: `2px solid ${theme.palette.secondary.main}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: theme.palette.secondary.dark,
                transform: 'translateY(-2px)',
                boxShadow: theme.palette.shadows.colored.primaryHover,
                borderColor: theme.palette.secondary.light
              }
            }}
          >
            💬 CONTACT US →
          </Button>
        </Box>
      </Container>
    </Box>
  )
}

export default ContactSection